

import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { ProjectZone } from '@/features/projects/types';
import { Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

/**
 * 工区リストセクションのプロパティ
 */
interface ZoneListSectionProps {
  zones: ProjectZone[];
  onZoneChange: (zones: ProjectZone[]) => void;
  isEditMode?: boolean; // 編集モードかどうか
  onResourceEditRequest?: (date: string, zoneId: string | null) => void;
}

/**
 * 工区リストセクション
 * @param param0 工区リストセクションのプロパティ
 * @returns 工区リストセクション
 */
export const ZoneListSection: React.FC<ZoneListSectionProps> = ({
  zones,
  onZoneChange,
  isEditMode = false
}) => {
  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneDescription, setNewZoneDescription] = useState('');
  const [newZoneArea, setNewZoneArea] = useState('');

  // 合計平米数を計算
  const totalArea = zones.reduce((sum, zone) => {
    const area = zone.area ?? zone.area_sqm ?? 0;
    return sum + (area || 0);
  }, 0);

  const handleZoneNameChange = (index: number, newName: string) => {
    if (!isEditMode) return;
    const updatedZones = [...zones];
    updatedZones[index] = { ...updatedZones[index], zone_name: newName };
    onZoneChange(updatedZones);
  };

  const handleZoneDescriptionChange = (index: number, newDescription: string) => {
    if (!isEditMode) return;
    const updatedZones = [...zones];
    updatedZones[index] = { ...updatedZones[index], notes: newDescription };
    onZoneChange(updatedZones);
  };

  const handleZoneAreaChange = (index: number, newArea: string) => {
    if (!isEditMode) return;
    const updatedZones = [...zones];
    const areaValue = newArea === '' ? null : parseInt(newArea, 10);
    const validAreaValue = areaValue && areaValue > 0 ? areaValue : null;
    updatedZones[index] = { ...updatedZones[index], area: validAreaValue ?? undefined, area_sqm: validAreaValue };
    onZoneChange(updatedZones);
  };

  const handleDeleteZone = (index: number) => {
    if (!isEditMode) return;
    const updatedZones = zones.filter((_, i) => i !== index);
    onZoneChange(updatedZones);
  };

  const handleAddZone = () => {
    if (!isEditMode || !newZoneName.trim()) return;
    const areaValue = newZoneArea === '' ? null : parseInt(newZoneArea, 10);
    const validAreaValue = areaValue && areaValue > 0 ? areaValue : null;

    const newZone: ProjectZone = {
      id: uuidv4(),
      tenant_id: 'default',
      project_id: '',
      name: newZoneName.trim(),
      zone_name: newZoneName.trim(),
      area_sqm: validAreaValue,
      area: validAreaValue,
      notes: newZoneDescription.trim() || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    onZoneChange([...zones, newZone]);
    setNewZoneName('');
    setNewZoneDescription('');
    setNewZoneArea('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">工区情報</h3>
        {zones.length > 0 && totalArea > 0 && (
          <div className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded-md">
            合計面積: <span className="font-medium text-foreground">{totalArea.toLocaleString()} ㎡</span>
          </div>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>工区名</TableHead>
            <TableHead>平米数 (㎡)</TableHead>
            <TableHead>説明</TableHead>
            {isEditMode && <TableHead className="w-[100px]">操作</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {zones.map((zone, index) => (
            <TableRow key={zone.id}>
              <TableCell>
                {isEditMode ? (
                  <Input
                    value={zone.zone_name}
                    onChange={(e) => handleZoneNameChange(index, e.target.value)}
                    className="w-full min-w-[120px]"
                  />
                ) : (
                  <span>{zone.zone_name}</span>
                )}
              </TableCell>
              <TableCell>
                {isEditMode ? (
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={(zone.area ?? zone.area_sqm) || ''}
                    onChange={(e) => handleZoneAreaChange(index, e.target.value)}
                    className="w-full min-w-[100px]"
                    placeholder="㎡"
                  />
                ) : (
                  <span>{(zone.area ?? zone.area_sqm) ? `${((zone.area ?? zone.area_sqm) as number).toLocaleString()} ㎡` : '-'}</span>
                )}
              </TableCell>
              <TableCell>
                {isEditMode ? (
                  <Input
                    value={zone.notes || ''}
                    onChange={(e) => handleZoneDescriptionChange(index, e.target.value)}
                    className="w-full min-w-[180px]"
                  />
                ) : (
                  <span>{zone.notes || '-'}</span>
                )}
              </TableCell>
              {isEditMode && (
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteZone(index)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {isEditMode && (
        <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
          <h4 className="font-medium">新規工区の追加</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="newZoneName">工区名</Label>
              <Input
                id="newZoneName"
                value={newZoneName}
                onChange={(e) => setNewZoneName(e.target.value)}
                placeholder="工区名"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newZoneArea">平米数 (任意)</Label>
              <Input
                id="newZoneArea"
                type="number"
                min="0"
                step="1"
                value={newZoneArea}
                onChange={(e) => setNewZoneArea(e.target.value)}
                placeholder="㎡"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newZoneDescription">説明 (任意)</Label>
              <Input
                id="newZoneDescription"
                value={newZoneDescription}
                onChange={(e) => setNewZoneDescription(e.target.value)}
                placeholder="説明"
              />
            </div>
          </div>
          <Button onClick={handleAddZone} className="gap-1">
            <Plus className="h-4 w-4" />
            追加
          </Button>
        </div>
      )}
    </div>
  );
}; 